import { useState, useMemo, useRef } from 'react';
import { WINAMP_TRACKS, BANNER_ADS } from '../parody-data.js';
import { useYouTubeAudioPlayer } from '../../hooks/useYouTubeAudioPlayer.js';

export const WinampGalaxyPage = ({ onNavigate }) => {
  const [downloadProgress, setDownloadProgress] = useState({});
  const progressBarRef = useRef(null);
  const {
    playerHostRef,
    currentTrack,
    isPlaying,
    volume,
    progressPercent,
    playbackError,
    formattedTime,
    selectTrackById,
    togglePlayPause,
    stopPlayback,
    nextTrack,
    prevTrack,
    seekToRatio,
    setVolume,
  } = useYouTubeAudioPlayer({
    tracks: WINAMP_TRACKS,
    initialTrackId: WINAMP_TRACKS[0]?.id,
    initialVolume: 75,
  });

  const handleDownload = (track) => {
    if (track.status === 'warning') {
      alert('WARNING: This file may contain a virus. Download anyway? (Just kidding, this is fake)');
      return;
    }
    setDownloadProgress(prev => ({ ...prev, [track.id]: 0 }));
    const interval = setInterval(() => {
      setDownloadProgress(prev => {
        const newProgress = (prev[track.id] || 0) + Math.random() * 15;
        if (newProgress >= 100) {
          clearInterval(interval);
          return { ...prev, [track.id]: 100 };
        }
        return { ...prev, [track.id]: newProgress };
      });
    }, 500);
  };

  const randomAd = useMemo(() => BANNER_ADS[Math.floor(Math.random() * BANNER_ADS.length)], []);

  return (
    <div className="winamp-page" data-testid="winamp-galaxy-page">
      {/* Header */}
      <div className="winamp-header">
        <div className="winamp-logo" onClick={() => onNavigate('portal')}>
          <span className="logo-icon">W</span>
          <span className="logo-text">WinScam Galaxy</span>
          <span className="logo-tagline">Now downloading: your dignity.exe</span>
        </div>
        <div className="winamp-nav">
          <a href="#">Downloads</a>
          <a href="#">Skins</a>
          <a href="#">Plugins</a>
          <a href="#">Community</a>
          <a href="#">Support</a>
        </div>
      </div>

      {/* Banner */}
      <div className="winamp-banner">
        <marquee style={{ backgroundColor: '#1a1a2e', color: '#00ff00' }}>
          Download unlimited MP3s! (Possibly Legal) Over 10 million tracks! Fast P2P - RIAA looking the other way! {randomAd.text}
        </marquee>
      </div>

      <div className="winamp-content">
        {/* Main Player */}
        <div className="winamp-player">
          <div className="player-window">
            <div className="player-titlebar">
              <span>WinScam Galaxy Player v2.91 [REGISTERED - pls dont sue]</span>
              <div className="titlebar-buttons">
                <button>_</button>
                <button>□</button>
                <button>×</button>
              </div>
            </div>
            
            <div className="player-display">
              <div className="visualizer">
                {[...Array(20)].map((_, i) => (
                  <div 
                    key={i} 
                    className="viz-bar" 
                    style={{ 
                      height: isPlaying ? `${Math.random() * 100}%` : '10%',
                      animationDelay: `${i * 0.05}s`
                    }}
                  />
                ))}
              </div>
              <div className="track-info">
                <div className="track-title">
                  {currentTrack?.title || 'Press Play to Start'}
                </div>
                <div className="track-artist">
                  {currentTrack?.artist || '---'}
                </div>
                <div className="track-time">
                  {formattedTime}
                </div>
                {playbackError && <div className="track-error">{playbackError}</div>}
              </div>
            </div>

            <div className="player-controls">
              <div className="control-row">
                <button className="ctrl-btn" onClick={prevTrack}>PREV</button>
                <button className="ctrl-btn" onClick={togglePlayPause}>
                  {isPlaying ? 'PAUSE' : 'PLAY'}
                </button>
                <button className="ctrl-btn" onClick={stopPlayback}>STOP</button>
                <button className="ctrl-btn" onClick={nextTrack}>NEXT</button>
                <button className="ctrl-btn">EJ</button>
              </div>
              <div className="volume-control">
                <span>Vol:</span>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                />
                <span>{volume}%</span>
              </div>
              <div
                className="progress-bar"
                ref={progressBarRef}
                onClick={(event) => {
                  if (!progressBarRef.current) return;
                  const rect = progressBarRef.current.getBoundingClientRect();
                  const ratio = (event.clientX - rect.left) / rect.width;
                  seekToRatio(ratio);
                }}
              >
                <div className="progress" style={{ width: `${progressPercent}%` }}></div>
              </div>
            </div>

            <div className="player-eq">
              <div className="eq-label">EQ</div>
              {['60', '170', '310', '600', '1k', '3k', '6k', '12k', '14k', '16k'].map((freq, i) => (
                <div key={i} className="eq-slider">
                  <input type="range" min="0" max="100" defaultValue={50 + Math.random() * 30} />
                  <span>{freq}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Playlist */}
          <div className="playlist-window">
            <div className="playlist-titlebar">
              <span>Playlist - {WINAMP_TRACKS.length} tracks</span>
            </div>
            <div className="playlist-content">
              {WINAMP_TRACKS.map((track, index) => (
                <div 
                  key={track.id}
                  className={`playlist-item ${currentTrack?.id === track.id ? 'active' : ''} ${track.status}`}
                  onClick={() => selectTrackById(track.id)}
                  data-testid={`track-${track.id}`}
                >
                  <span className="track-num">{index + 1}.</span>
                  <span className="track-name">{track.title}</span>
                  <span className="track-size">{track.size}</span>
                  <span className={`track-status ${track.status}`}>
                    {track.status === 'downloading' && 'DL'}
                    {track.status === 'downloaded' && 'OK'}
                    {track.status === 'warning' && 'WARN'}
                    {track.status === 'seeding' && 'UP'}
                    {track.status === 'error' && 'ERR'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div className="download-section">
          <h2>Hot Downloads</h2>
          <p className="warning-text">
            DISCLAIMER: All downloads are totally legal and definitely not pirated. WinScam Galaxy accepts no liability for subpoenas, lawsuits, or corrupted hard drives.
          </p>
          
          <div className="download-list">
            {WINAMP_TRACKS.map((track) => (
              <div key={track.id} className="download-item">
                <div className="download-info">
                  <span className="dl-icon">MP3</span>
                  <div className="dl-details">
                    <span className="dl-title">{track.title}</span>
                    <span className="dl-meta">{track.artist} • {track.size}</span>
                  </div>
                </div>
                <div className="download-action">
                  {downloadProgress[track.id] !== undefined ? (
                    <div className="download-progress">
                      <div className="progress-bar-mini">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${downloadProgress[track.id]}%` }}
                        />
                      </div>
                      <span>{Math.round(downloadProgress[track.id])}%</span>
                    </div>
                  ) : (
                    <button 
                      className={`download-btn ${track.status}`}
                      onClick={() => handleDownload(track)}
                    >
                      {track.status === 'warning' ? 'Warning Download' : 'Download'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="speed-indicator">
            <span>Download Speed: </span>
            <span className="speed-value">56.6 kbps</span>
            <span className="speed-note">(Estimated time: 47 minutes)</span>
          </div>
        </div>

        {/* Sidebar */}
        <div className="winamp-sidebar">
          <div className="sidebar-box">
            <h3>Popular Skins</h3>
            <ul className="skin-list">
              <li>Skull & Bones Dark</li>
              <li>Rainbow Classic</li>
              <li>Robot Chrome</li>
              <li>Flame Red</li>
              <li>Crystal Blue</li>
            </ul>
          </div>

          <div className="sidebar-ad">
            <div className="ad-box" style={{ backgroundColor: randomAd.color }}>
              {randomAd.text}<br/>
              <small>{randomAd.subtext}</small>
            </div>
          </div>

          <div className="sidebar-box">
            <h3>Top Charts</h3>
            <ol className="chart-list">
              <li>Umbrella - Rihanna</li>
              <li>Crank That - Soulja Boy</li>
              <li>Girlfriend - Avril Lavigne</li>
              <li>Beautiful Girls - Sean Kingston</li>
              <li>Hey There Delilah - Plain White T's</li>
            </ol>
          </div>

          <div className="sidebar-box warning">
            <h3>Legal Notice</h3>
            <p className="legal-text">
              By using WinScam Galaxy, you agree that any resemblance to actual
              file-sharing services is purely coincidental. We definitely don't
              know what a "torrent" is. WinScam Galaxy is a subsidiary of Profit
              Dynamics International™. Your IP has been logged for safekeeping.
            </p>
          </div>
        </div>
      </div>
      <div className="youtube-audio-engine-host" aria-hidden="true">
        <div ref={playerHostRef} />
      </div>

      {/* Footer */}
      <div className="winamp-footer">
        <button className="back-btn" onClick={() => onNavigate('portal')}>Back to Portal</button>
        <p>© 2007 WinScam Galaxy — Not affiliated with LimeWire, Kazaa, or Napster... or are we? A Profit Dynamics International™ entity. Download responsibly (you won't).</p>
      </div>
    </div>
  );
};

export default WinampGalaxyPage;
